import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import TypeSkeleton from './TypeSkeleton';
import FormHelperText from '../FormHelperText';
import { useTranslation } from 'react-i18next';
import LabeledBox from '../../special/LabeledBox';
import ExoDialog from '../../exo/ExoDialog';
import ExoAvatar from '../../exo/ExoAvatar';
import DBSearch from '../../special/DBSearch';
import CardGrid from '../../cards/layout/CardGrid';
import MediaPreview from '../../exo/explorer/MediaPreview';
import { tokens } from '../../../global/theme/tokens';
import DeleteIcon from '@mui/icons-material/Delete';
import Cookies from 'universal-cookie';

const TypeProjectTimeTracking = ({
  updateValidationOnChange,
  dataCollection,
  errors,
  field,
  size,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const raw = dataCollection?.[field.key];

    if (!raw) {
      setSelected(null);
      return;
    }

    const incoming = normalizeInput(raw);

    // Only update if the new value differs from the current selection
    setSelected((prevSelected) => {
      if (
        !prevSelected ||
        JSON.stringify(prevSelected) !== JSON.stringify(incoming)
      ) {
        return incoming;
      }
      return prevSelected;
    });
  }, [dataCollection, field.key]);

  //making proper structure if only there is project and NO workpackage.
  function normalizeInput(data) {
    if (!data) return null;

    // If it's already normalized with a project (and maybe workPackage)
    if (data.project && typeof data.project === 'object') {
      return data;
    }

    // If it's a plain project
    if ('id' in data && ('name' in data || 'label' in data)) {
      return { project: data };
    }
    return null; // Catch-all fallback
  }

  function handleChangeSelect(data) {
    const normalizedData = normalizeInput(data);
    setSelected(normalizedData);

    updateValidationOnChange(field.key, normalizedData);
    setOpen(false);
  }

  return (
    <Box
      sx={{
        gridColumn: field.colspan
          ? `span ${field.colspan} / span ${field.colspan}`
          : 'auto',
      }}
      className="flex w-full gap-2"
    >
      <LabeledBox
        className="w-full"
        label={field.label}
        onClick={() => setOpen(true)}
      >
        {selected && (
          <Box className="flex flex-wrap items-center gap-2 min-w-0">
            {selected.workPackage ? (
              <>
                <Box className="flex items-center gap-1 min-w-0 truncate">
                  {selected.project?.image?.temporaryUrl && (
                    <MediaPreview
                      src={selected.project.image.temporaryUrl}
                      size="30px"
                    />
                  )}
                  <span className="truncate">
                    {selected.project?.name || selected.name || selected.label}
                  </span>
                </Box>
                <span className="shrink-0">→</span>
                <Box className="flex items-center gap-1 min-w-0">
                  {selected.workPackage.icon?.temporaryUrl && (
                    <MediaPreview
                      src={selected.workPackage.icon.temporaryUrl}
                      size="30px"
                    />
                  )}
                  <span className="min-w-0 truncate">
                    {selected.workPackage?.title}
                  </span>
                </Box>
              </>
            ) : (
              <Box className="flex items-center gap-2 w-full min-w-0 h-[30px]">
                {selected.project?.image?.temporaryUrl ? (
                  <MediaPreview
                    src={selected.project.image.temporaryUrl}
                    size="30px"
                  />
                ) : null}

                <span className="truncate min-w-0 flex items-center h-full">
                  {selected.project?.name || selected.name || selected.label}
                </span>
              </Box>
            )}
          </Box>
        )}
      </LabeledBox>

      {selected && (
        <Button
          onClick={() => {
            setSelected(null);
            updateValidationOnChange(field.key, undefined);
          }}
          sx={{
            padding: 0,
            width: size === 'small' ? '40px' : '53px',
            bgcolor: colors.bgInput,
            minWidth: '0px',
            border: '1px solid transparent',
            ':hover': {
              bgcolor: colors.error + '30',
              border: `1px solid ${colors.error}80`,
              color: colors.error,
            },
          }}
        >
          <DeleteIcon />
        </Button>
      )}
      <FormHelperText />
      <ExoDialog open={open} onClose={() => setOpen(false)}>
        <SelectProjectWorkPackage
          types={field.types}
          onSelect={handleChangeSelect}
          onClose={() => setOpen(false)}
          fieldUserId={field.userId}
          field={field}
        />
      </ExoDialog>
    </Box>
  );
};

const SelectProjectWorkPackage = ({
  onSelect,
  onClose,
  types,
  fieldUserId,
  field,
}) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const [projectStatus, setProjectStatus] = useState('own');
  const [filter, setFilter] = useState('');

  const config = useConfig();

  // With this setting active, time corrections may only reference assigned
  // projects, so the "All Projects" toggle is hidden and the list stays "own"
  const restrictToOwn =
    Boolean(field?.inTimeCorrection) &&
    Boolean(
      Number(config?.timesheet?.restrictTimeCorrectionsToAssignedProjects),
    );

  //Get request for specific project
  const handleProjectSelect = (project) => {
    if (!config || !project?.id) return;

    DBRequest({
      config,
      path: `projects/${project.id}`,
      method: API_GET,
      onResponse: (projectData) => {
        setSelectedProject(projectData.data);
      },
    });
  };

  useEffect(() => {
    const newFilter =
      projectStatus === 'own'
        ? `status = 0 AND teamMemberIds = ${fieldUserId || userId}`
        : 'status = 0';

    setFilter(newFilter);
  }, [projectStatus]);

  return (
    <Box className="flex flex-col gap-4 overflow-y-auto h-full">
      <DBSearch param="projects" onSearch={setProjects} filter={filter}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">{t('Projects')}</Typography>
          {!restrictToOwn && (
            <ToggleButtonGroup
              size="small"
              value={projectStatus}
              exclusive
              onChange={(e) => setProjectStatus(e.target.value)}
              aria-label="Project Status"
              className="backdrop-blur-lg mt-2"
            >
              <ToggleButton value="own">{t('Own')}</ToggleButton>
              <ToggleButton value="all">{t('All Projects')}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        <CardGrid>
          {projects ? (
            projects.map((project, index) => (
              <Project
                key={index}
                project={project}
                types={types}
                onSelect={handleProjectSelect}
                onClose={onClose}
              />
            ))
          ) : (
            <TypeSkeleton n={25} />
          )}
        </CardGrid>
      </DBSearch>
      <ProjectPhaseList
        onSelect={onSelect}
        selectedProject={selectedProject}
        onClose={() => setSelectedProject(null)}
        types={types}
        field={field}
        restrictToOwn={restrictToOwn}
      />
    </Box>
  );
};

//Projects list
const Project = ({ project, onSelect, types }) => {
  return (
    <Button
      onClick={() => onSelect(project)}
      variant="contained"
      color="secondary"
      sx={{
        transition: 'scale 0.3s ease',
      }}
      className=" cursor-pointer w-full flex flex-col backdrop-blur-lg items-center p-4 rounded-lg"
    >
      <ExoAvatar
        picture={project.image}
        type={'company'}
        name={project.subLabel}
        size="90px"
      />
      <Typography variant="h3" className="pt-4">
        <b>{project.nr}</b>
      </Typography>
      <Typography
        variant="h6"
        className="pt-4 break-words text-center"
        sx={{
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {project.label}
      </Typography>
    </Button>
  );
};

// List the Phases and workPackages
const PhaseWorkPackagesList = ({ phase, onSelect, types }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <>
      <Box className="rounded-lg px-2 py-1">
        <Typography variant="h4" className="pb-4">
          {phase.workPackages.length > 0 ? phase.name : ''}
        </Typography>
        <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
          {phase.workPackages.map((workPackage) => (
            <Box
              key={workPackage.id}
              onClick={() => onSelect?.(workPackage, phase)}
              className="rounded-lg flex flex-col items-center p-4 cursor-pointer"
              sx={{
                borderColor: colors.card,
                borderWidth: '1px',
                ':hover': {
                  borderColor: colors.success,
                  backgroundColor: colors.success + '30',
                },
                bgcolor: colors.card,
              }}
            >
              {workPackage.icon && (
                <MediaPreview
                  src={workPackage.icon.temporaryUrl}
                  size="140px"
                />
              )}
              <Typography variant="h5" className="mt-2 text-center">
                {workPackage.title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

// Get Phases and workPackages of the selected project
const ProjectPhaseList = ({
  selectedProject,
  onClose,
  onSelect,
  types,
  field,
  restrictToOwn,
}) => {
  const config = useConfig();
  const [phases, setPhases] = useState(null);

  const { t } = useTranslation();

  const cookies = new Cookies();
  // When creating on behalf of another user, assignment checks must use the
  // target user's id, not the logged-in user's cookie id
  const userId = Number(field?.userId ?? cookies.get('userId'));

  const [wpShowStatus, setWpShowStatus] = useState('running');
  const [shownPhases, setShownPhases] = useState(null);
  const [noWorkPackages, setNoWorkPackages] = useState(false);

  // With this setting active, a bare project (without a work package) is not
  // a valid time correction target, so instead of auto-selecting the project
  // when no work package is selectable, an empty state is shown
  const requireWorkPackage =
    Boolean(field?.inTimeCorrection) &&
    Boolean(Number(config?.timesheet?.requireWorkPackageForTimeTracking));

  function handleWpShowStatusChange(newStatus, phases) {
    // filter wp if time correction is active, otherwise show all wps
    const newPhases = phases.data.map((phase) => ({
      ...phase,
      workPackages: field.inTimeCorrection
        ? [
            ...phase.workPackages.filter((wp) => {
              return wp.isTimeTrackable === 1;
            }),
          ]
        : [...phase.workPackages],
    }));

    if (newStatus === 'running') {
      for (let i = 0; i < newPhases.length; i++) {
        newPhases[i].workPackages = newPhases[i].workPackages.filter((el) => {
          // Normal behaviour (existing functionality)
          if (!field?.inTimeCorrection) {
            return el.status != 1;
          }

          // Special behaviour for time correction
          const isUserAssigned = el.users?.some((user) => user.id === userId);
          return el.status != 1 && isUserAssigned;
        });
      }
    }

    // Special handling when time correction is active
    if (field?.inTimeCorrection) {
      const hasAnyWorkPackages = newPhases.some(
        (phase) => phase.workPackages.length > 0,
      );

      if (!hasAnyWorkPackages) {
        if (newStatus === 'running' && !restrictToOwn) {
          // No running WPs assigned to the user (e.g. a project they are not
          // part of) -> fall back to the "All" view instead of closing
          handleWpShowStatusChange('all', phases);
        } else if (requireWorkPackage) {
          // No selectable WPs at all and a WP is mandatory -> the project is
          // not a valid target, show an empty state instead of selecting it
          setNoWorkPackages(true);
        } else {
          // No selectable WPs at all -> auto-select the bare project
          onSelect?.(selectedProject);
          onClose?.();
        }
        return;
      }
    }

    setWpShowStatus(newStatus);
    setShownPhases({ data: newPhases });
  }

  useEffect(() => {
    if (!config || !selectedProject) return;

    setNoWorkPackages(false);

    DBRequest({
      config,
      path: `projects/${selectedProject.id}/phases`,
      method: API_GET,
      onResponse: (data) => {
        setPhases(data);

        // Auto-select project if no phases or work packages
        const hasNoPhases = !data?.data?.length;
        const hasNoWorkPackages = data?.data?.every(
          (phase) => !phase.workPackages || phase.workPackages.length === 0,
        );

        if (hasNoPhases || hasNoWorkPackages) {
          if (requireWorkPackage) {
            // A bare project is not a valid time correction target
            setNoWorkPackages(true);
            return;
          }
          onSelect?.(selectedProject); // Automatically select the project
          onClose?.(); // Close the dialog
        }
        handleWpShowStatusChange('running', data);
      },
    });
  }, [config, selectedProject]);

  if (!noWorkPackages && (!shownPhases || !shownPhases.data?.length))
    return null; // Avoid flash

  if (noWorkPackages)
    return (
      <ExoDialog open={Boolean(selectedProject)} onClose={onClose}>
        <Box className="h-full flex flex-col items-center justify-center gap-2 p-4 text-center">
          <Typography variant="h4">
            {t('No work packages available')}
          </Typography>
          <Typography variant="h6">
            {t(
              'A work package is required to request a time correction for this project',
            )}
          </Typography>
        </Box>
      </ExoDialog>
    );

  return (
    <ExoDialog open={Boolean(selectedProject)} onClose={onClose}>
      {!restrictToOwn && (
        <Box className="w-full flex justify-end pr-6 pt-1">
          <ToggleButtonGroup
            value={wpShowStatus}
            onChange={(e, newVal) => {
              if (newVal) {
                handleWpShowStatusChange(newVal, phases);
              }
            }}
            size="small"
            exclusive
            aria-label="Project Status"
            className="backdrop-blur-lg mt-2"
          >
            <ToggleButton value={'running'}>{t('Running & Own')}</ToggleButton>
            <ToggleButton value={'all'}>{t('All')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <Box className="h-full flex flex-col overflow-y-auto p-4">
        <Box className="flex flex-col gap-5">
          {shownPhases.data.map((phase) => (
            <PhaseWorkPackagesList
              key={phase.id}
              phase={phase}
              types={types}
              onSelect={(workPackage) =>
                onSelect?.({ workPackage, project: selectedProject })
              }
            />
          ))}
        </Box>
      </Box>
    </ExoDialog>
  );
};

export default TypeProjectTimeTracking;
